import React, { useRef, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    IconButton,
    Box,
    Chip,
    Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import * as monaco from 'monaco-editor';
import { PackageHistoryDto } from '../../../api/models/PackageDto';
import { COMPARED_FIELDS, ComparedField, formatFieldValue } from '../utils/comparisonUtils';

interface PackageComparisonDialogProps {
    open: boolean;
    onClose: () => void;
    selectedVersions: string[];
    history: PackageHistoryDto[];
    hasChanges: boolean;
}

const PackageComparisonDialog: React.FC<PackageComparisonDialogProps> = ({
    open,
    onClose,
    selectedVersions,
    history,
    hasChanges
}) => {
    const editorsRef = useRef<monaco.editor.IStandaloneDiffEditor[]>([]);

    const getSelectedVersionsData = useCallback(() => {
        const versions = selectedVersions.map(validFrom => {
            const found = history.find(h => h.validFrom === validFrom);
            return found;
        }).filter(Boolean) as PackageHistoryDto[];

        return versions.sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime());
    }, [selectedVersions, history]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const disposeEditors = useCallback(() => {
        editorsRef.current.forEach(editor => {
            try { editor.dispose(); } catch { /* noop */ }
        });
        editorsRef.current = [];
    }, []);

    const handleClose = () => {
        disposeEditors();
        onClose();
    };

    useEffect(() => {
        if (!open) {
            disposeEditors();
        }
    }, [open, disposeEditors]);

    const mountEditor = useCallback((container: HTMLDivElement | null, field: ComparedField) => {
        if (!container || selectedVersions.length !== 2) return;

        const versions = getSelectedVersionsData();
        if (versions.length !== 2) return;

        if ((container as any).__mounted) return;
        (container as any).__mounted = true;

        const original = formatFieldValue(versions[0], field);
        const modified = formatFieldValue(versions[1], field);
        const language = field.isJson ? 'json' : 'plaintext';

        const hasDiff = original !== modified;

        try {
            monaco.editor.setTheme('vs');

            if (hasDiff) {
                const diffEditor = monaco.editor.createDiffEditor(container);
                diffEditor.setModel({
                    original: monaco.editor.createModel(original, language),
                    modified: monaco.editor.createModel(modified, language),
                });
                editorsRef.current.push(diffEditor);
            } else {
                const editor = monaco.editor.create(container, {
                    value: original,
                    language,
                    readOnly: true,
                    automaticLayout: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    fontSize: 12,
                    lineNumbers: 'on',
                    renderWhitespace: 'selection',
                }) as any;
                editorsRef.current.push(editor);
            }
        } catch (error) {
            console.error(`Error creating editor for ${field.key}:`, error);
        }
    }, [selectedVersions, getSelectedVersionsData]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                style: {
                    height: '95vh',
                    maxHeight: '95vh',
                    width: '95vw',
                    maxWidth: '95vw',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                Configuration Comparison
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{ color: 'grey.500' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    px: 3,
                    py: 1,
                    height: 'calc(100% - 80px)',
                    overflowY: 'auto',
                }}
            >
                {selectedVersions.length === 2 && (
                    <>
                        <Box sx={{ pb: 2, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <DialogContentText>
                                {!hasChanges ? (
                                    <Chip
                                        label="No differences found"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontSize: '1.1rem', p: 1 }}
                                    />
                                ) : (
                                    <>
                                        Comparing version from {formatDate(selectedVersions[0])} (left)
                                        {' '}with version from {formatDate(selectedVersions[1])} (right)
                                    </>
                                )}
                            </DialogContentText>
                        </Box>

                        {COMPARED_FIELDS.map((field) => (
                            <Box key={field.key} sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {field.label}
                                </Typography>
                                <Box
                                    ref={(el: HTMLDivElement | null) => mountEditor(el, field)}
                                    sx={{
                                        height: field.isJson ? '300px' : '80px',
                                        border: 1,
                                        borderColor: 'grey.300',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        '& .monaco-editor, & .monaco-diff-editor': {
                                            width: '100% !important',
                                            height: '100% !important',
                                        },
                                    }}
                                />
                            </Box>
                        ))}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PackageComparisonDialog;